import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface IconUploadOptions {
  file: File;
  targetSize: { width: number; height: number };
  iconType: 'icon72' | 'icon96' | 'icon120' | 'icon128' | 'icon144' | 'icon152' | 'icon180' | 'icon192' | 'icon384' | 'icon512' | 'appleTouchIcon';
}

interface UploadResult {
  url: string;
  success: boolean;
  error?: string;
}

export const usePWAIconUpload = () => {
  const [isUploading, setIsUploading] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  const validateAndResizeImage = async (
    file: File,
    targetWidth: number,
    targetHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      img.onload = () => {
        // Set canvas dimensions to target size
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Draw image to canvas, scaling to fit
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: 'image/png',
                lastModified: Date.now(),
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Failed to create resized image'));
            }
          },
          'image/png',
          0.9
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const uploadIcon = async ({ file, targetSize, iconType }: IconUploadOptions): Promise<UploadResult> => {
    setIsUploading(prev => ({ ...prev, [iconType]: true }));

    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      // Resize image to target dimensions
      const resizedFile = await validateAndResizeImage(
        file,
        targetSize.width,
        targetSize.height
      );

      // Generate unique filename
      const fileExt = 'png';
      const fileName = `pwa-${iconType}-${Date.now()}.${fileExt}`;
      const filePath = `pwa-icons/${fileName}`;

      // Upload to Supabase storage
      const { data, error } = await supabase.storage
        .from('logos')
        .upload(filePath, resizedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      toast({
        title: "Icon uploaded successfully",
        description: `${iconType} has been uploaded and resized to ${targetSize.width}x${targetSize.height}`,
      });

      return {
        url: urlData.publicUrl,
        success: true
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      
      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive",
      });

      return {
        url: '',
        success: false,
        error: errorMessage
      };
    } finally {
      setIsUploading(prev => ({ ...prev, [iconType]: false }));
    }
  };

  return {
    uploadIcon,
    isUploading
  };
};